import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BarChart, MessageSquare, BookOpen, Brain,
  ArrowRight
} from 'lucide-react';
import LearnerStatsDashboard from './LearnerStatsDashboard';

type DashboardOverviewProps = {
  userName: string;
  userRole: string;
  progress: number;
  xp: number;
  onChatWithMentor: () => void;
};

const mockStats = {
  progress: {
    overall: 65,
    modules: { completed: 8, total: 12 },
    skillLevel: 'Intermediate'
  },
  performance: {
    averageScore: 85,
    latestScore: 92,
    trend: 'up' as const
  },
  engagement: {
    xp: 1250,
    nextLevelXp: 2000,
    rank: 125,
    totalUsers: 1000
  },
  time: {
    total: '45h 30m',
    recommended: '2h/day',
    daily: '1h 30m'
  },
  strengths: [
    'Problem Analysis',
    'Decision Making',
    'Technical Documentation'
  ],
  improvements: [
    'Stakeholder Communication',
    'Time Management',
    'Risk Assessment'
  ],
  feedback: {
    latest: 'Great progress on your latest project! Your problem-solving approach shows significant improvement.',
    nextSteps: [
      'Complete the advanced decision-making module',
      'Practice stakeholder presentations',
      'Review risk management fundamentals'
    ]
  }
};

const DashboardOverview: React.FC<DashboardOverviewProps> = ({
  userName,
  userRole,
  progress,
  xp,
  onChatWithMentor
}) => {
  // Format role for display
  const formatRole = (role: string) => {
    const roles: Record<string, string> = {
      'product-manager': 'Product Manager',
      'developer': 'Desarrollador',
      'entrepreneur': 'Emprendedor',
      'tech-entrepreneur': 'Tech Entrepreneur',
      'consultant': 'Consultor',
      'project-manager': 'Project Manager'
    };
    return roles[role] || role;
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-4 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">¡Bienvenido, {userName}!</h2>
            <p className="mt-1 text-white/90">
              Continúa tu viaje como {formatRole(userRole)}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-white/20 px-3 py-1 rounded-full">
              <span className="text-white font-medium">{xp} XP</span>
            </div>
            <button
              onClick={onChatWithMentor}
              className="bg-white text-purple-600 px-4 py-2 rounded-lg hover:bg-white/90 transition-colors flex items-center shadow-md hover:shadow-lg"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Mentor IA
            </button>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-white/90">Progreso general</span>
            <span className="text-sm font-medium text-white/90">{progress}%</span>
          </div>
          <div className="h-2 bg-white/20 rounded-full">
            <div
              className="h-2 bg-white rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Stats Dashboard */}
      <LearnerStatsDashboard stats={mockStats} />
    </div>
  );
};

export default DashboardOverview;